import { Command } from 'cmdk';
import { useNavigate } from 'react-router';
import { Search, Sparkles, Clock, FileText, ArrowRight, Loader2, X } from 'lucide-react';
import { useSearch } from '../../hooks/useSearch';
import { useSearchHistory } from '../../hooks/useSearchHistory';
import { useState } from 'react';

interface SearchCoreProps {
  mode: 'overlay' | 'inline';
  initialValue?: string;
  onSelect?: () => void;
}

export function SearchCore({ mode, initialValue = '', onSelect }: SearchCoreProps) {
  const [query, setQuery] = useState(initialValue);
  const { patternMatches, pages, aiSuggestions, isLoadingAI } = useSearch(query);
  const { history, add, clear } = useSearchHistory();
  const navigate = useNavigate();

  function handleSelect(route: string, label: string, type: string) {
    add({ query: query || label, type, route, label });
    navigate(route);
    onSelect?.();
  }

  const isEmpty = query.trim() === '';
  const hasPatterns = patternMatches.length > 0;
  const hasAI = aiSuggestions && aiSuggestions.suggestions.length > 0;

  return (
    <Command
      className={`${mode === 'overlay' ? 'rounded-2xl border border-ring shadow-float' : ''} bg-surface-raised overflow-hidden`}
      shouldFilter={!hasPatterns && !hasAI}
    >
      <div className="flex items-center gap-2 border-b border-ring px-4">
        <Search className="h-4 w-4 text-ink-tertiary shrink-0" />
        <Command.Input
          value={query}
          onValueChange={setQuery}
          placeholder="Search stores, users, or ask a question..."
          className="flex-1 bg-transparent py-3.5 text-sm text-ink placeholder:text-ink-tertiary outline-none"
          autoFocus
        />
        {isLoadingAI && <Loader2 className="h-4 w-4 animate-spin text-accent shrink-0" />}
        {query && (
          <button onClick={() => setQuery('')} className="p-1 hover:bg-surface-sunken rounded-md">
            <X className="h-3.5 w-3.5 text-ink-tertiary" />
          </button>
        )}
      </div>

      <Command.List
        className={`overflow-y-auto p-2 ${mode === 'overlay' ? 'max-h-80' : 'max-h-[60vh]'}`}
      >
        <Command.Empty className="py-8 text-center text-sm text-ink-tertiary">
          No results found.
        </Command.Empty>

        {/* Pattern Matches */}
        {hasPatterns && (
          <Command.Group heading="Quick Navigation">
            {patternMatches.map((match) => (
              <Command.Item
                key={match.route}
                value={match.label}
                onSelect={() => handleSelect(match.route, match.label, 'pattern')}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm cursor-pointer aria-selected:bg-accent-subtle transition-colors"
              >
                <match.provider.icon className="h-4 w-4 text-accent shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-ink">{match.label}</span>
                  <span className="ml-2 text-ink-tertiary">{query}</span>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-ink-tertiary shrink-0" />
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {/* AI Suggestions */}
        {hasAI && (
          <Command.Group heading="AI Suggestions">
            {aiSuggestions!.suggestions.map((suggestion, idx) => (
              <Command.Item
                key={`ai-${idx}`}
                value={suggestion.label}
                onSelect={() => handleSelect(suggestion.route, suggestion.label, 'ai')}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm cursor-pointer aria-selected:bg-accent-subtle transition-colors"
              >
                <Sparkles className="h-4 w-4 text-brand-orange shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-ink">{suggestion.label}</span>
                  <p className="text-xs text-ink-tertiary truncate">{suggestion.description}</p>
                </div>
                <ArrowRight className="h-3.5 w-3.5 text-ink-tertiary shrink-0" />
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {/* Pages (fuzzy match) */}
        {!isEmpty && (
          <Command.Group heading="Pages">
            {pages.map((page) => (
              <Command.Item
                key={page.id}
                value={`${page.label} ${page.keywords?.join(' ') ?? ''}`}
                onSelect={() => handleSelect(page.path, page.label, 'page')}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm cursor-pointer aria-selected:bg-accent-subtle transition-colors"
              >
                {page.icon ? (
                  <page.icon className="h-4 w-4 text-ink-tertiary shrink-0" />
                ) : (
                  <FileText className="h-4 w-4 text-ink-tertiary shrink-0" />
                )}
                <span className="flex-1 text-ink">{page.label}</span>
                <ArrowRight className="h-3.5 w-3.5 text-ink-tertiary shrink-0" />
              </Command.Item>
            ))}
          </Command.Group>
        )}

        {/* Recent searches */}
        {isEmpty && history.length > 0 && (
          <Command.Group
            heading={
              <span className="flex items-center justify-between">
                Recent
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    clear();
                  }}
                  className="text-2xs text-ink-tertiary hover:text-ink font-normal"
                >
                  Clear
                </button>
              </span>
            }
          >
            {history.map((entry) => (
              <Command.Item
                key={`${entry.route}-${entry.timestamp}`}
                value={entry.label}
                onSelect={() => handleSelect(entry.route, entry.label, entry.type)}
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm cursor-pointer aria-selected:bg-accent-subtle transition-colors"
              >
                <Clock className="h-4 w-4 text-ink-tertiary shrink-0" />
                <span className="flex-1 text-ink">{entry.label}</span>
                <span className="text-2xs text-ink-tertiary">{entry.query}</span>
              </Command.Item>
            ))}
          </Command.Group>
        )}
      </Command.List>

      <div className="border-t border-ring px-4 py-2 flex items-center gap-4 text-2xs text-ink-tertiary">
        <span>
          <kbd className="rounded bg-surface-sunken px-1.5 py-0.5 font-mono">↑↓</kbd> navigate
        </span>
        <span>
          <kbd className="rounded bg-surface-sunken px-1.5 py-0.5 font-mono">↵</kbd> select
        </span>
        <span>
          <kbd className="rounded bg-surface-sunken px-1.5 py-0.5 font-mono">esc</kbd> close
        </span>
      </div>
    </Command>
  );
}
